import { useCallback, useEffect, useState } from "react";

const EXPIRY_DAYS = 7; // Keep dry run IDs for 7 days

interface DryRunJobData {
  jobRunId: string;
  timestamp: number;
}

export const useDryRunJobStorage = (jobId: string, storageKeyPrefix: string) => {
  const storageKey = `${storageKeyPrefix}${jobId}`;
  const [dryRunJobRunIds, setDryRunJobRunIds] = useState<Set<string>>(new Set());

  // Load dry run job IDs from localStorage on mount
  useEffect(() => {
    const loadDryRunJobIds = () => {
      try {
        const stored = localStorage.getItem(storageKey);
        if (!stored) return;

        const data: DryRunJobData[] = JSON.parse(stored);
        const now = Date.now();
        const expiryTime = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

        // Filter out expired entries
        const validData = data.filter((item) => now - item.timestamp < expiryTime);

        // Update localStorage if we removed any expired entries
        if (validData.length !== data.length) {
          localStorage.setItem(storageKey, JSON.stringify(validData));
        }

        setDryRunJobRunIds(new Set(validData.map((item) => item.jobRunId)));
      } catch {
        // Silently fail to load from localStorage
      }
    };

    loadDryRunJobIds();
  }, [storageKey]);

  // Save a new dry run job ID
  const saveDryRunJobId = useCallback(
    (jobRunId: string) => {
      // Always update in-memory state so dry-run classification works
      // even when localStorage is unavailable
      setDryRunJobRunIds((prev) => new Set(prev).add(jobRunId));

      try {
        const stored = localStorage.getItem(storageKey);
        const data: DryRunJobData[] = stored ? JSON.parse(stored) : [];

        if (!data.some((item) => item.jobRunId === jobRunId)) {
          data.push({
            jobRunId,
            timestamp: Date.now(),
          });
          localStorage.setItem(storageKey, JSON.stringify(data));
        }
      } catch {
        // localStorage may be unavailable; in-memory state is still updated above
      }
    },
    [storageKey],
  );

  // Clear all dry run job IDs for this job
  const clearDryRunJobIds = useCallback(() => {
    setDryRunJobRunIds(new Set());

    try {
      localStorage.removeItem(storageKey);
    } catch {
      // localStorage may be unavailable; in-memory state is still cleared above
    }
  }, [storageKey]);

  return {
    dryRunJobRunIds,
    saveDryRunJobId,
    clearDryRunJobIds,
  };
};
